import type { TerrainType, FortificationType, PlayerUnitType } from './types';

// =====================================================
// Terrain Constants
// =====================================================

export const TERRAIN_TYPES: Record<TerrainType, { name: string; cover: number }> = {
  OPEN: { name: 'Open', cover: 0 },
  WOODS: { name: 'Woods', cover: 1 },
  FOXHOLE: { name: 'Foxhole', cover: 2 },
  BUNKER: { name: 'Bunker', cover: 3 },
  DEPTH_BUNKER: { name: 'Depth Bunker', cover: 3 },
};

export const FORTIFICATION_TYPES: Record<FortificationType, { name: string; blockMovement: boolean }> = {
  WIRE: { name: 'Barbed Wire', blockMovement: true },
  MINE: { name: 'Mine', blockMovement: false },
  MINEFIELD: { name: 'Minefield', blockMovement: true },
  DEPTH_BUNKER: { name: 'Depth Bunker', blockMovement: true },
};

// =====================================================
// Unit Constants
// =====================================================

export interface UnitTypeInfo {
  name: string;
  abbreviation: string;
  movement: number;
  fireRange: number;
  diceCount: number;
  hitNumber: number;
  canMoveInEnemyResponse: boolean;
  canFireInEnemyResponse: boolean;
  canRally: boolean;
  imageKey: string;
}

export const UNIT_TYPES: Record<PlayerUnitType, UnitTypeInfo> = {
  // Infantry
  INFANTRY_SQUAD: {
    name: 'Infantry Squad',
    abbreviation: 'INF',
    movement: 2,
    fireRange: 4,
    diceCount: 4,
    hitNumber: 4,
    canMoveInEnemyResponse: true,
    canFireInEnemyResponse: true,
    canRally: true,
    imageKey: 'infantry',
  },
  WEAPONS_TEAM: {
    name: 'Weapons Team',
    abbreviation: 'WPN',
    movement: 2,
    fireRange: 6,
    diceCount: 3,
    hitNumber: 3,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: true,
    canRally: true,
    imageKey: 'weapons',
  },
  ENGINEER: {
    name: 'Engineer',
    abbreviation: 'ENG',
    movement: 2,
    fireRange: 4,
    diceCount: 3,
    hitNumber: 4,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: true,
    canRally: true,
    imageKey: 'engineer',
  },
  RECON: {
    name: 'Recon',
    abbreviation: 'RCN',
    movement: 3,
    fireRange: 4,
    diceCount: 3,
    hitNumber: 4,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: true,
    imageKey: 'recon',
  },

  // Armor
  TANK: {
    name: 'Tank',
    abbreviation: 'TNK',
    movement: 3,
    fireRange: 8,
    diceCount: 6,
    hitNumber: 3,
    canMoveInEnemyResponse: true,
    canFireInEnemyResponse: true,
    canRally: true,
    imageKey: 'tank',
  },
  TANK_DESTROYER: {
    name: 'Tank Destroyer',
    abbreviation: 'TD',
    movement: 2,
    fireRange: 8,
    diceCount: 5,
    hitNumber: 3,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: true,
    canRally: true,
    imageKey: 'tank_destroyer',
  },
  SP_GUN: {
    name: 'SP Gun',
    abbreviation: 'SPG',
    movement: 1,
    fireRange: 10,
    diceCount: 5,
    hitNumber: 3,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: true,
    imageKey: 'sp_gun',
  },

  // Fire Support
  ARTILLERY: {
    name: 'Artillery',
    abbreviation: 'ARTY',
    movement: 1,
    fireRange: 99, // Anywhere on map
    diceCount: 4,
    hitNumber: 4,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: true,
    imageKey: 'artillery',
  },
  MORTAR: {
    name: 'Mortar',
    abbreviation: 'MTR',
    movement: 1,
    fireRange: 99,
    diceCount: 3,
    hitNumber: 4,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: true,
    imageKey: 'mortar',
  },

  // Attacker Fire Support Assets
  GPMG_TEAM: {
    name: 'GPMG Team',
    abbreviation: 'GPMG',
    movement: 0,
    fireRange: 6,
    diceCount: 6,
    hitNumber: 3,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: false,
    imageKey: 'gpmg',
  },
  RIFLE_PLATOON: {
    name: 'Rifle Platoon',
    abbreviation: 'RFL',
    movement: 0,
    fireRange: 6,
    diceCount: 10,
    hitNumber: 4,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: false,
    imageKey: 'rifle_platoon',
  },
  MORTAR_TEAM: {
    name: 'Mortar Team',
    abbreviation: 'MTR',
    movement: 0,
    fireRange: 99,
    diceCount: 4,
    hitNumber: 4,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: false,
    imageKey: 'mortar_team',
  },
  ILLUMINATION: {
    name: 'Illumination',
    abbreviation: 'ILLUM',
    movement: 0,
    fireRange: 99,
    diceCount: 0,
    hitNumber: 0,
    canMoveInEnemyResponse: false,
    canFireInEnemyResponse: false,
    canRally: false,
    imageKey: 'illumination',
  },
};

// =====================================================
// Cover Modifiers
// =====================================================

export const COVER_MODIFIERS: Record<number, { name: string; modifier: number }> = {
  0: { name: 'None', modifier: 0 },
  1: { name: 'Light', modifier: 1 },
  2: { name: 'Medium', modifier: 2 },
  3: { name: 'Heavy', modifier: 3 },
};

// =====================================================
// Hit Numbers by Terrain
// =====================================================

// To hit = base hit number + cover modifier
// Higher is harder to hit
export const HIT_NUMBERS: Record<TerrainType, number> = {
  OPEN: 4, // 4+ to hit
  WOODS: 5, // 5+ to hit ( woods cover)
  FOXHOLE: 5, // 5+ to hit ( foxhole cover)
  BUNKER: 6, // 6+ to hit ( bunker cover)
  DEPTH_BUNKER: 6, // 6+ to hit ( depth bunker cover)
};

// =====================================================
// Rally Modifiers
// =====================================================

export const RALLY_BASE_NUMBER = 4; // 4+ to rally
export const RALLY_WOODS_BONUS = 1; // +1 to rally roll if in woods

// =====================================================
// Game Phase Constants
// =====================================================

export const PHASES = {
  MOVEMENT: 'MOVEMENT',
  FIREFIGHT: 'FIREFIGHT',
  ENEMY_RESPONSE: 'ENEMY_RESPONSE',
  RALLY: 'RALLY',
  GAME_OVER: 'GAME_OVER',
} as const;

export const PHASE_ORDER = ['MOVEMENT', 'FIREFIGHT', 'ENEMY_RESPONSE', 'RALLY'] as const;

export const PHASE_DESCRIPTIONS: Record<string, string> = {
  MOVEMENT: 'Move your fresh units across the battlefield',
  FIREFIGHT: 'Fire with your fresh units at enemy targets',
  ENEMY_RESPONSE: 'Defender may move and fire with responding units',
  RALLY: 'Attempt to rally suppressed and spent units',
};

// =====================================================
// Victory Conditions
// =====================================================

export const VICTORY_REASON_DESCRIPTIONS: Record<string, string> = {
  OBJECTIVE_CAPTURED: 'The objective has been captured!',
  TURN_LIMIT: 'Turn limit reached - defender wins!',
  ELIMINATION: 'All enemy units eliminated!',
};

// =====================================================
// Default Scenario Configuration
// =====================================================

export const DEFAULT_TURN_LIMIT = 16;
export const DEFAULT_HEX_WIDTH = 12;
export const DEFAULT_HEX_HEIGHT = 10;

// =====================================================
// Asset Limits
// =====================================================

export const FIRE_SUPPORT_ASSETS = {
  GPMG: { maxUses: 3, diceCount: 6, hitNumber: 3 },
  RIFLE_PLATOON: { maxUses: 2, diceCount: 10, hitNumber: 4 },
  MORTAR_HE: { maxUses: 4, diceCount: 4, hitNumber: 4 },
  MORTAR_SMOKE: { maxUses: 2, diceCount: 0, hitNumber: 0 }, // Smoke doesn't cause hits
  ILLUMINATION: { maxUses: 3, diceCount: 0, hitNumber: 0 },
} as const;

// =====================================================
// Night Attack Special Rules
// =====================================================

export const NIGHT_ATTACK_PENALTY = 1; // -1 to all dice rolls at night
export const ILLUMINATION_DURATION_TURNS = 1; // Illumination lasts for current turn

// =====================================================
// Animation Durations (ms)
// =====================================================

export const ANIMATION_DURATION = {
  HEX_HOVER: 200,
  DICE_ROLL: 500,
  UNIT_MOVE: 300,
  REVEAL: 400,
};

// =====================================================
// WebSocket Reconnect Settings
// =====================================================

export const WS_RECONNECT_DELAY = 1000; // Initial reconnect delay
export const WS_MAX_RECONNECT_DELAY = 5000; // Max reconnect delay
export const WS_RECONNECT_ATTEMPTS = 10; // Max reconnect attempts

// =====================================================
// Storage Keys
// =====================================================

export const STORAGE_KEYS = {
  PLAYER_ID: 'tth_player_id',
  PLAYER_NAME: 'tth_player_name',
  CURRENT_GAME_ID: 'tth_game_id',
  ASSIGNED_SIDE: 'tth_assigned_side',
} as const;
